import {Component} from '@angular/core';
import {Store} from '@ngrx/store';
import {
  DealershipService,
  DealerUtilsService,
  OssScheduledService,
  PreferredDealerActions,
  selectActiveVehicle,
  selectGarage,
  selectProfile,
  TrackingService
} from '@ox/data';
import {Dealer, GarageVehicles, Profile, Vehicle, Widget} from '@ox/typings';
import {AppConfigService, AutoDestroy, getAddressString} from '@ox/utils';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {combineLatest, Subject, take, takeUntil, tap} from 'rxjs';
import {LocateDealerWebuComponent} from '../../dealer/locate-dealer/locate-dealer-webu.component';

const nullDealer = {
  id: null,
  name: null,
  street: null,
  city: null,
  citySubdivision: null,
  state: null,
  zipCode: null,
  salesPhone: null,
  servicePhone: null,
  dealerWebSite: null,
  scheduleServiceWebSite: null,
  specialtyServices: null,
  distance: null,
  latitude: null,
  longitude: null,
  dealerCode: null,
  makeCodes: null,
  sellingBrandCode: null,
  bac: null,
  countryCode: null,
  departments: null,
  programs: null,
  certifiedInternet: null,
  address: null,
  addressLine2: null,
  rank: null,
  markerIcon: null,
  preferredDealer: null
} as unknown as Dealer;

@Component({
  selector: 'webu-dealer-accessories',
  templateUrl: './dealer-accessories.component.html',
  providers: [OssScheduledService, DealershipService]
})
export class DealerAccessoriesComponent {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  locateDealer = LocateDealerWebuComponent;
  showDealerLocator = false;
  pendingScheduledServices: any = [];
  add = 0;
  activeVinAppts: any = [];
  apptLimit = 1;
  allActiveAppts = false;
  isMEO: boolean;
  bsModalRef?: BsModalRef;
  dealership: Dealer | undefined;
  maxApptsDisplayed = 5;
  hasPreferredDealer = false;
  hasUpcomingService = false;
  widget = {
    market: this.ocAppConfig.bml.market,
    locale: this.ocAppConfig.locale,
    state: 'loading',
    driverType: '',
    dealer: {}
  } as Widget;
  scheduledServices = {
    currentActiveVehicle: {
      serviceLaneVisit: {
        vehicle: {
          vin: ''
        }
      }
    }
  };
  garage: GarageVehicles;
  garage$ = this.store.select(selectGarage).pipe(takeUntil(this.destroy$), tap(
    (garage) => this.garage = garage
  ));
  activeVehicle: Vehicle;
  activeVehicle$ = this.store.select(selectActiveVehicle).pipe(takeUntil(this.destroy$), tap(
    (vehicle) => (vehicle) ? this.activeVehicle = vehicle : undefined
  ));
  profile: Profile;
  profile$ = this.store.select(selectProfile).pipe(takeUntil(this.destroy$), tap(
    (profile) => profile ? this.profile = profile : undefined
  ));
  dealer$ = combineLatest([this.profile$, this.activeVehicle$]).pipe(takeUntil(this.destroy$), tap(
    () => (this.profile && this.activeVehicle) ? this.loadPreferredDealer() : undefined
  ));
  appts$ = combineLatest([this.profile$, this.garage$, this.activeVehicle$]).pipe(
    takeUntil(this.destroy$), tap(
    () => (this.profile.gcin && this.garage && this.activeVehicle && this.pendingScheduledServices.length === 0) ? this.loadProfileAppts() : this.displayAppts()
  ));

  constructor(protected ocAppConfig: AppConfigService,
              private ocDealershipService: DealershipService,
              private ocOssService: OssScheduledService,
              private ocTrackingService: TrackingService,
              private store: Store,
              private dealerUtilsService: DealerUtilsService) {
    this.garage$.subscribe();
    this.activeVehicle$.subscribe();
    this.profile$.subscribe();
    this.dealer$.subscribe();
    this.appts$.subscribe();
  }

  get userAddress() {
    if (!this.profile || !this.profile.address1) return '';
    return getAddressString({
      address1: this.profile.address1,
      address2: (this.profile as any)?.street || this.profile.address2,
      city: this.profile.city,
      state: this.profile.stateProvince,
      zip: (this.profile as any)?.zipCode || this.profile.postalCode
    });
  }

  changeThis({recordLocator, bac}: { recordLocator: string, bac: string }) {
    window.location.href = `../service/edit?serviceItem=${recordLocator}&bac=${bac}`;
  }

  // Toggle function for show all button
  toggle(show: string) {
    if (show === 'showall') {
      this.apptLimit = this.activeVinAppts?.length;
      this.allActiveAppts = true;
      setTimeout(() => {
        document.getElementById('showLessButton')?.focus();
      }, 0);
    }
    if (show === 'showless') {
      this.apptLimit = 1;
      this.allActiveAppts = false;
      setTimeout(() => {
        document.getElementById('showAllButton')?.focus();
      }, 0);
    }
  }

  loadOss() {
    let url;
    if (this.hasPreferredDealer === true && this.dealership != undefined) {
      url = this.dealerUtilsService.getScheduleServiceUrl(this.dealership);
    } else {
      url = this.dealerUtilsService.getScheduleServiceUrl();
    }
    window.location.href = url;
  }

  // This is going to define the variables for display
  // pendingScheduleServices controls this.widget.state
  displayAppts() {
    this.hasUpcomingService = false;
    this.add = 0;
    this.activeVinAppts = [];
    this.widget.state = 'loading';
    if (this.pendingScheduledServices.length > 0) {
      for (let i = 0; i < this.maxApptsDisplayed; i++) {
        if (this.pendingScheduledServices[i]?.serviceLaneVisit.vehicle.vin === this.activeVehicle?.vin) {
          this.widget.state = 'loaded';
          this.hasUpcomingService = true;
          this.activeVinAppts[this.add] = this.pendingScheduledServices[i];
          this.activeVinAppts[this.add].dealership.address.postalZone =
            this.activeVinAppts[this.add].dealership.address.postalZone.substring(0, 5) + '-' + this.activeVinAppts[this.add].dealership.address.postalZone.substring(5);
          this.activeVinAppts[this.add].dealership.departments[0].phoneNumber =
            '(' + this.activeVinAppts[this.add].dealership.departments[0].phoneNumber.substring(0, 3) + ')' + this.activeVinAppts[this.add].dealership.departments[0].phoneNumber.substring(3, 6) + '-' + this.activeVinAppts[this.add].dealership.departments[0].phoneNumber.substring(6);
          this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start =
            this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 5);

          if (+(this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 2)) < 12) {
            if (+(this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 2)) >= 0) {
              this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start += ' AM';
            }
          } else {
            if (+(this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 2)) > 12) {
              const convert: any = this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.split(':');
              convert[0] -= 12;
              this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start = convert.join(':');
            }
            this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start += ' PM';
          }

          this.adoptDate(this.activeVinAppts[this.add].serviceLaneVisit.appointmentDate);
          this.add++;
        }
      }
      // This is where we need to check if there are no service appointments for this vehicle.
      // If at the end of the above for loop the array for the active appts is empty there are no appts to display
      if (this.activeVinAppts?.length === 0) {
        this.widget.state = 'loaded';
        this.hasUpcomingService = false;
      } else {
        this.activeVinAppts = this.sortAppts(this.activeVinAppts);
      }
    } else {
      this.widget.state = 'loaded';
      this.hasUpcomingService = false; //There are no services to display
    }
  }

  loadProfileAppts() {
    const success = (data: any) => {
      if (!data) {
        throw new Error('No OSS Data');
      } else {
        this.pendingScheduledServices = data;
      }
    };

    const failed = (error: any) => {
      throw new Error(error);
    };

    this.ocOssService.upcomingScheduledService(this.widget.market, this.profile.gcin)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => success(response),
        error: (response: any) => failed(response),
        complete: () => this.displayAppts()
    });
  }

  loadPreferredDealer() {
    this.hasPreferredDealer = false;
    let returnDealer:Dealer;
    const onFailure = (error: any) => {
      this.dealership = undefined;
      this.hasPreferredDealer = false;
      this.widget.state = 'cannotLoad';
      this.ocTrackingService.trackEvent('event25', 'preferredDealerWidget.cannotLoad');
    };

    const onSuccess = (data: Dealer) => {
      if (data && data.id) {
        this.hasPreferredDealer = true;
        this.dealership = data;
        this.widget.dealer = data;
        this.widget.dealer.address = getAddressString({
          address1: data.name,
          address2: data.street,
          city: data.city,
          state: data.state,
          zip: data.zipCode
        });
        returnDealer = this.widget.dealer;
      } else {
        this.hasPreferredDealer = false;
        returnDealer = nullDealer;
      }
      this.store.dispatch(PreferredDealerActions.update({dealer: returnDealer}));
    };

    this.ocDealershipService.preferredDealer$(this.activeVehicle)
      .pipe(take(1))
      .subscribe({
        next: onSuccess,
        error: onFailure
      });
  }

  onBeginSearch() {
    this.showDealerLocator = true;
  }

  //Pulls the date from CMS for appointment display
  adoptDate(date: any) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    date = date.split('-');
    this.activeVinAppts[this.add].day = date[2];
    this.activeVinAppts[this.add].year = date[0];
    this.activeVinAppts[this.add].month = 'scheduled_service_' + months[parseInt(date[1]) - 1];
  }

  sortAppts(activeVinAppts: any) {
    const sortedArr = activeVinAppts.sort((n1: any, n2: any) => {
      if ((new Date(n1.serviceLaneVisit.appointmentDate).getTime() < (new Date(n2.serviceLaneVisit.appointmentDate).getTime()))) return -1;
      if ((new Date(n1.serviceLaneVisit.appointmentDate).getTime() > (new Date(n2.serviceLaneVisit.appointmentDate).getTime()))) return 1;
      if (n1.serviceLaneVisit.appointmentTime.start < n2.serviceLaneVisit.appointmentTime.start) return -1;
      if (n1.serviceLaneVisit.appointmentTime.start > n2.serviceLaneVisit.appointmentTime.start) return 1;

      return 0;
    });

    return sortedArr;
  }

}
